/* eslint-disable max-classes-per-file */
import { ExtraChargeAgodaInterface } from "@/app/[lang]/hotels/booking/components/HotelExtraChargeComponent/HotelExtraChargeComponent";
import {
  ProductTypeEnums,
  RequestBedTypeEnums,
} from "@/types/product/account/history";

import { ResponsePresenter } from "../../common/commonClassPresenter";
import { CorporateDisCountPresenter } from "../../corporate/corporateServiceInterfaces/corporateInterface";
import {
  HotelLocationPresenter,
  HotelRoomCriteriaPresenter,
  HotelRoomInfoPresenter,
} from "./getHistoryHotelListInterface";
import {
  ContactDatePresenter,
  DayPlanDataPresenter,
  ExpirePaymentDatePresenter,
  PaymentChannelDataPresenter,
  PaymentCouponDiscountPresenter,
  RefundedPaymentDatePresenter,
} from "./historyBookingCommonInterface";

class HotelDetailImportantNotePresenter {
  remark: string;

  childAndExtrabedPolicy: {
    infant: string;
    children: string;
    adult: string;
    childrenStayFree: boolean;
    minGuestAge: string;
  };

  constructor(response: any) {
    this.remark = response?.remark || "";
    this.childAndExtrabedPolicy = {
      infant: response?.childAndExtrabedPolicy?.infant || "",
      children: response?.childAndExtrabedPolicy?.children || "",
      adult: response?.childAndExtrabedPolicy?.adult || "",
      childrenStayFree:
        response?.childAndExtrabedPolicy?.childrenStayFree || false,
      minGuestAge: response?.childAndExtrabedPolicy?.minGuestAge || "",
    };
  }
}

export class HotelReviewRatingPresenter {
  numReviews: number;

  rating: number;

  textRatingDetail: string;

  constructor(res: any) {
    this.numReviews = res?.numReviews || 0;
    this.rating = res?.rating || 0;
    this.textRatingDetail = res?.textRatingDetail || "";
  }
}

export class HotelDetailHotelPresenter {
  id: string;

  name: string;

  nameEn: string;

  slug: string;

  description: string;

  shaPlus: boolean;

  shaExtraPlus: boolean;

  phone: string;

  fax: string;

  email: string;

  addressFull: string;

  cover: string;

  gallery: string[];

  reviewRating: HotelReviewRatingPresenter;

  isGreenCertificate: boolean;

  extraCharge: ExtraChargeAgodaInterface[];

  location: HotelLocationPresenter;

  isAgodaPolicy: boolean;

  importantNote: HotelDetailImportantNotePresenter;

  starRating: number;

  constructor(response: any) {
    this.id = response?.id || "";
    this.name = response?.name || "";
    this.nameEn = response?.nameEn || "";
    this.slug = response?.slug || "";
    this.description = response?.description || "";
    this.shaPlus = response?.shaPlus || false;
    this.shaExtraPlus = response?.shaExtraPlus || false;
    this.phone = response?.phone || "";
    this.fax = response?.fax || "";
    this.email = response?.email || "";
    this.addressFull = response?.addressFull || "";
    this.cover = response?.cover || "";
    this.gallery = response?.gallery || [];
    this.reviewRating = new HotelReviewRatingPresenter(
      response?.reviewRating || {},
    );
    this.isGreenCertificate = response?.isGreenCertificate || false;
    this.location = new HotelLocationPresenter(response?.location);
    this.extraCharge = (response?.extraCharge || []).map(
      (results: any) => new ExtraChargeAgodaInterface(results),
    );
    this.isAgodaPolicy = response?.isAgodaPolicy || false;
    this.importantNote = new HotelDetailImportantNotePresenter(
      response?.importantNote,
    );
    this.starRating = response?.starRating || 0;
  }
}

class HotelDetailPaymentPresenter {
  couponDiscount: PaymentCouponDiscountPresenter;

  bookingDate: string;

  paymentSuccessDate: string;

  isPaylater: boolean;

  paymentType: string;

  paymentUrl: string;

  isAllocatesExpire: boolean;

  isPaymentExpire: boolean;

  expireAllocateDate: ExpirePaymentDatePresenter;

  expirePaymentDate: ExpirePaymentDatePresenter;

  channel: PaymentChannelDataPresenter;

  price: {
    priceWithoutVat: number;
    vat: number;
    totalAmount: number;
    currency: string;
  };

  refunded: RefundedPaymentDatePresenter;

  constructor(response: any) {
    this.paymentType = response?.paymentType || "";
    this.paymentUrl = response?.paymentUrl || "";
    this.isPaymentExpire = response?.isPaymentExpire || false;
    this.isAllocatesExpire = response?.isAllocatesExpire || false;
    this.expireAllocateDate = new ExpirePaymentDatePresenter(
      response?.expireAllocateDate,
    );
    this.expirePaymentDate = new ExpirePaymentDatePresenter(
      response?.expirePaymentDate,
    );
    this.channel = new PaymentChannelDataPresenter(response?.channel);
    this.couponDiscount = new PaymentCouponDiscountPresenter(
      response?.couponDiscount,
    );
    this.isPaylater = response?.isPaylater || false;
    this.bookingDate = response?.bookingDate || "";
    this.paymentSuccessDate = response?.paymentSuccessDate || "";
    this.price = {
      priceWithoutVat: response?.price?.priceWithoutVat,
      vat: response?.price?.vat,
      totalAmount: response?.price?.totalAmount,
      currency: response?.price?.currency,
    };
    this.refunded = new RefundedPaymentDatePresenter(response?.refunded);
  }
}

class HotelDetailGuestPresenter {
  titlename: string;

  firstname: string;

  lastname: string;

  email: string;

  countryOfPassport: string;

  specialRequest: string;

  request: {
    smoking: boolean;
    bedType: string;
  };

  countryOfPassportName: string;

  constructor(response: any) {
    this.titlename = response?.titlename || "";
    this.firstname = response?.firstname || "";
    this.lastname = response?.lastname || "";
    this.email = response?.email || "";
    this.countryOfPassport = response?.countryOfPassport || "";
    this.specialRequest = response?.specialRequest || "";
    this.countryOfPassportName = response?.countryOfPassportName || "";
    this.request = {
      smoking: response?.request?.smoking || false,
      bedType: (
        response?.request?.bedType || RequestBedTypeEnums.KINGSIZE
      ).toLocaleLowerCase(),
    };
  }
}

class HotelDetailPolicyPresenter {
  freeCancellation: boolean;

  cancellationPolicy: string;

  policyForShow: string[];

  datePolicy: string;

  timePolicy: string;

  datePolicyOri: string;

  policyBreakDown: string;

  remark: string;

  alert: string;

  dateCancel: string;

  constructor(response: any) {
    this.freeCancellation = response?.freeCancellation || false;
    this.cancellationPolicy = response?.cancellationPolicy || "";
    this.policyForShow = response?.policyForShow || [];
    this.datePolicy = response?.datePolicy || "";
    this.timePolicy = response?.timePolicy || "";
    this.datePolicyOri = response?.datePolicyOri || "";
    this.policyBreakDown = response?.policyBreakDown || "";
    this.remark = response?.remark || "";
    this.alert = response?.alert || "";
    this.dateCancel = response?.dateCancel || "";
  }
}

class HotelDetailRoomPresenter {
  roomCriteria: HotelRoomCriteriaPresenter;

  roomInfo: HotelRoomInfoPresenter;

  specialDeal: Array<string>;

  constructor(response: any) {
    this.roomCriteria = new HotelRoomCriteriaPresenter(response?.roomCriteria);
    this.roomInfo = new HotelRoomInfoPresenter(response?.roomInfo);
    this.specialDeal = response?.specialDeal || [];
  }
}

export class HotelBenefits {
  public type: string = "";

  public name: string = "";

  public detail: string = "";

  constructor(data?: HotelBenefits) {
    this.detail = data?.detail ?? this.detail;
    this.name = data?.name ?? this.name;
    this.type = data?.type ?? this.type;
  }
}

export class HotelDetailDataPresenter extends CorporateDisCountPresenter {
  productType: string;

  bookingInternalNo: string;

  pinCode: string;

  reservationNo: string;

  price: number;

  currency: string;

  bookingStep: string;

  bookingStatus: string;

  paymentStatus: string;

  buttonCancelStatus: boolean;

  buttonResendPayment: boolean;

  buttonResendVoucher: boolean;

  hotel: HotelDetailHotelPresenter;

  contact: ContactDatePresenter;

  payment: HotelDetailPaymentPresenter;

  guest: HotelDetailGuestPresenter;

  policy: HotelDetailPolicyPresenter;

  room: HotelDetailRoomPresenter;

  dayPlan: DayPlanDataPresenter;

  dateCancel: string;

  refundStatus: string;

  isPayAtHotel: boolean;

  paylaterDeadlineDate: string;

  paylaterDeadlineDateLang: string;

  paylaterChargedDate: string;

  isConjobsFail: boolean;

  type: string;

  benefits: HotelBenefits[] = [];

  statusManualBooking: boolean;

  constructor(response: any) {
    super(response);
    this.productType = response?.productType || ProductTypeEnums.HOTEL;
    this.bookingInternalNo = response?.bookingInternalNo || "";
    this.pinCode = response?.pinCode || "";
    this.reservationNo = response?.reservationNo || "";
    this.price = response?.price || 0;
    this.currency = response?.currency || "";
    this.bookingStep = (response?.bookingStep || "").toLocaleLowerCase();
    this.bookingStatus = (response?.bookingStatus || "").toLocaleLowerCase();
    this.paymentStatus = (response?.paymentStatus || "").toLocaleLowerCase();
    this.buttonCancelStatus = response?.buttonCancelStatus || false;
    this.buttonResendPayment = response?.buttonResendPayment || false;
    this.buttonResendVoucher = response?.buttonResendVoucher || false;
    this.hotel = new HotelDetailHotelPresenter(response?.hotel);
    this.contact = new ContactDatePresenter(response?.contact);
    this.payment = new HotelDetailPaymentPresenter(response?.payment);
    this.guest = new HotelDetailGuestPresenter(response?.guest);
    this.policy = new HotelDetailPolicyPresenter(response?.policy);
    this.room = new HotelDetailRoomPresenter(response?.room);
    this.dayPlan = new DayPlanDataPresenter(response?.dayPlan);
    this.dateCancel = response?.dateCancel || "";
    this.refundStatus = response?.refundStatus || "";
    this.isPayAtHotel = response?.isPayAtHotel || false;
    this.paylaterDeadlineDate = response?.paylaterDeadlineDate || "";
    this.paylaterDeadlineDateLang = response?.paylaterDeadlineDateLang || "";
    this.paylaterChargedDate = response?.paylaterChargedDate || "";
    this.isConjobsFail = response?.isConjobsFail || false;
    this.type = response?.type || ProductTypeEnums.HOTEL;
    this.benefits = response?.benefits ?? this.benefits;
    this.statusManualBooking =
      (response?.statusManualBooking ?? response?.statusManaualBooking) ||
      false;
  }
}

export class ResponseGetDetailHotelPresenter extends ResponsePresenter<HotelDetailDataPresenter> {
  cToken: string;

  constructor(response: any) {
    super(response, (data) => new HotelDetailDataPresenter(data));
    this.cToken = response?.cToken || "";
  }
}
